import React, { useState } from 'react';

import { Col, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT, RANGES } from 'app-constants/date';
import { OPTION_KEYS_FOR_SELECT } from 'app-constants/select-options';

import { DatePicker, MultiDatePicker } from 'components';
import { ContentFilterProps } from 'hoc/withFilter';
import { TransitsArchiveFilterValues, TransitsIntensityTrafficFilterValues } from 'store/wim/types/transits';

import MultiDatePickerDateRender from 'components/MultiDatePicker/MultiDatePickerDateRender';
import MultiDatePickerFooter from 'components/MultiDatePicker/MultiDatePickerFooter';
import { StyledSelect, StyledFilter, StyledDateWrapper } from './styles';
import { useIntensityTrafficFilter } from './hooks/use-intensity-traffic-filter';

export const INITIAL_TRANSITS_FILTER_VALUES: Partial<TransitsArchiveFilterValues> = {
  start: dayjs().startOf('day').valueOf(),
  end: dayjs().endOf('day').valueOf(),
};

const IntensityTrafficFilter: React.FC<ContentFilterProps<TransitsIntensityTrafficFilterValues>> = ({
  filterManager,
}) => {
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { Option } = Select;
  const { t } = useTranslation();

  const { onPlatformNameChange, onRangePickerChange, onMultiDatePickerChange, state, platforms } =
    useIntensityTrafficFilter(filterManager);
  const { notWorkedDates, start, end, wimIds } = state;

  const ranges = {
    [t('date.hour')]: RANGES.getHourRange(),
    [t('date.day')]: RANGES.getDayRange(),
    [t('date.week')]: RANGES.getWeekRange(),
    [t('date.month')]: RANGES.getMonthRange(),
    [t('date.year')]: RANGES.getYearRange(),
    [t('date.quarter')]: RANGES.getQuarterRange(),
  };

  const initialDateRange: [Dayjs, Dayjs] = [
    start ? dayjs(start) : dayjs().startOf('day'),
    end ? dayjs(end) : dayjs().endOf('day'),
  ];

  const onOkClickHandler = () => {
    setIsOpen(false);
  };
  const onCancelClickHandler = () => {
    onMultiDatePickerChange([]);
    setIsOpen(false);
  };

  const dateRender = (current: Dayjs) => {
    let isSelected = false;
    if (notWorkedDates) {
      isSelected = notWorkedDates.some((date) => dayjs(date).isSame(current, 'day'));
    } else {
      isSelected = false;
    }
    return <MultiDatePickerDateRender currentDate={current} isSelected={isSelected} />;
  };

  const footerRender = () => {
    return <MultiDatePickerFooter OkClick={onOkClickHandler} cancelClick={onCancelClickHandler} />;
  };

  return (
    <StyledFilter>
      <Col span={5}>
        <StyledSelect
          maxTagTextLength={20}
          maxTagCount={1}
          allowClear
          value={wimIds}
          showSearch
          placeholder={t('filter.allPlatforms')}
          optionFilterProp="children"
          onChange={onPlatformNameChange}
        >
          {platforms?.map((platform) => (
            <Option key={platform.uid} value={platform.uid}>
              {platform.wim}
            </Option>
          ))}
        </StyledSelect>
      </Col>
      <Col span={12} offset={1}>
        <StyledDateWrapper>
          <span>{t('filter.analysisPeriod')}: </span>
          <DatePicker.RangePicker
            clearIcon={false}
            value={initialDateRange}
            showTime={{ format: DATE_FORMAT.RANGE_PICKER_TIME }}
            format={DATE_FORMAT.RANGE_PICKER}
            ranges={ranges as Record<string, [Dayjs | null, Dayjs | null]>}
            onChange={onRangePickerChange}
          />
          <span>{t('filter.notWorked')}: </span>
          <MultiDatePicker
            selectedDates={notWorkedDates?.map((date) => dayjs(date)) || []}
            onSelectNotWorkedDates={onMultiDatePickerChange}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            dateRender={dateRender}
            footerRender={footerRender}
          />
        </StyledDateWrapper>
      </Col>
    </StyledFilter>
  );
};

export default IntensityTrafficFilter;
